// SessionsList.jsx — the AI Form Builder's home screen: past builder
// sessions (one document each) with status, resumable drafts first.

import { T } from '../../theme';
import { Button, Badge } from '../fmxkit';

const STATUS_BADGE = {
  extracting:  { color: 'blueLight', label: 'Reading…' },
  scoping:     { color: 'orange',    label: 'Needs scope' },
  drafting:    { color: 'blueDark',  label: 'Draft' },
  pushed:      { color: 'green',     label: 'Pushed' },
  rolled_back: { color: 'gray',      label: 'Rolled back' },
  error:       { color: 'red',       label: 'Failed' },
};

const KIND_ICON = { pdf: 'file-pdf', image: 'image', docx: 'file-word', text: 'align-left' };

export default function SessionsList({ sessions, loading, siteUrl, hasCreds, onNew, onResume, onDelete }) {
  return (
    <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 4 }}>
        <div>
          <div style={{ font: `700 18px/24px ${T.fontSans}`, color: T.ink }}>AI Form Builder</div>
          <div style={{ font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted, marginTop: 3, maxWidth: 640 }}>
            Upload a document that represents a form — a work order, request slip, inspection
            checklist, or data sheet — and the builder reads it, proposes the FMX custom fields it
            implies, and previews exactly what would be created on {siteUrl || 'the project’s FMX site'}.
          </div>
        </div>
        <Button variant="primary" icon="wand-magic-sparkles" onClick={onNew} disabled={!hasCreds}>
          New from document
        </Button>
      </div>
      {!hasCreds && (
        <div style={{ margin: '10px 0 0', padding: '8px 12px', background: T.tint.warning, borderRadius: T.radiusBtn, font: `500 12px/17px ${T.fontSans}`, color: T.orangeText }}>
          Connect this project’s FMX site (Settings → FMX credentials) first — the builder validates
          every field against the live site before anything is proposed.
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        {loading ? (
          <p style={{ color: T.inkSoft, fontSize: 13 }}>Loading sessions…</p>
        ) : sessions.length === 0 ? (
          <p style={{ color: T.inkSoft, fontSize: 13 }}>No builder sessions yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Document', 'Status', 'Fieldsets', 'Updated', ''].map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${T.border}`, font: `700 12px/16px ${T.fontSans}`, color: T.inkMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => {
                const badge = STATUS_BADGE[s.status] || STATUS_BADGE.error;
                const resumable = s.status === 'scoping' || s.status === 'drafting';
                return (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${T.borderSubtle}` }}>
                    <td style={{ padding: '9px 10px', color: T.ink }}>
                      <i className={`fa-solid fa-${KIND_ICON[s.source_kind] || 'file'}`} style={{ color: T.inkSoft, width: 18 }} />
                      {s.source_filename || `(${s.source_kind})`}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <Badge color={badge.color}>{badge.label}</Badge>
                    </td>
                    <td style={{ padding: '9px 10px', color: T.inkMuted }}>
                      {(s.scope?.fieldsets || []).join(', ') || '—'}
                    </td>
                    <td style={{ padding: '9px 10px', color: T.inkMuted, whiteSpace: 'nowrap' }}>
                      {s.updated_at ? new Date(s.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {(resumable || s.status === 'error' || s.status === 'extracting') && (
                        <button className="fmx-btn-xs" onClick={() => onResume(s)} style={{ marginRight: 6 }}>
                          {resumable ? 'Resume' : 'View'}
                        </button>
                      )}
                      <button className="fmx-btn-xs" title="Delete session" onClick={() => onDelete(s)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
